import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

/* ============================================================================
 * БАЗОВЫЕ ЭЛЕМЕНТЫ КОМПОЗИЦИИ
 * Модульная структура: панель, метрика, поле, кнопка, вкладки, разделитель.
 * ========================================================================= */

/** Панель с заголовком */
export function Panel({
  title,
  icon: Icon,
  actions,
  children,
  className = '',
  bodyClassName = '',
  state,
}: {
  title: string
  icon?: LucideIcon
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
  /** Подсветка панели состоянием */
  state?: ReactNode
}) {
  return (
    <section className={`ut-panel flex min-h-0 flex-col ${className}`}>
      <header className="ut-panel-header shrink-0">
        {Icon && <Icon size={13} strokeWidth={2} className="shrink-0" aria-hidden />}
        <span className="truncate">{title}</span>
        {state}
        <span className="flex-1" />
        {actions}
      </header>
      <div className={`min-h-0 flex-1 overflow-auto ${bodyClassName}`}>{children}</div>
    </section>
  )
}

/** Крупная метрика со значением и подписью */
export function Metric({
  label,
  value,
  unit,
  icon: Icon,
  state,
  title,
}: {
  label: string
  value: ReactNode
  unit?: string
  icon?: LucideIcon
  /** Подсветка значением состояния */
  state?: 'active' | 'selected' | 'critical-error' | 'warning' | 'pending' | 'approved'
  title?: string
}) {
  return (
    <div
      className="ut-stated ut-state-critical-error rounded-md border px-2.5 py-1.5"
      data-state={state ?? 'active'}
      title={title}
      style={
        state
          ? undefined
          : { borderColor: 'var(--ut-color-line)', background: 'var(--ut-color-surface-alt)' }
      }
    >
      <div className="ut-metric-label flex items-center gap-1">
        {Icon && <Icon size={10} strokeWidth={2} aria-hidden />}
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="ut-metric-value">{value}</span>
        {unit && <span className="text-xs text-ink-muted">{unit}</span>}
      </div>
    </div>
  )
}

/** Поле ввода с подписью */
export function Field({
  label,
  children,
  hint,
  error,
}: {
  label: string
  children: ReactNode
  hint?: string
  error?: string
}) {
  return (
    <label className="block">
      <span className="ut-label">{label}</span>
      {children}
      {hint && !error && <span className="mt-0.5 block text-[10px] text-ink-muted">{hint}</span>}
      {error && <span className="mt-0.5 block text-[10px] text-critical">{error}</span>}
    </label>
  )
}

/** Кнопка с вариантом оформления */
export function Button({
  children,
  onClick,
  variant = 'default',
  size = 'md',
  disabled,
  icon: Icon,
  title,
  type = 'button',
  full,
  className = '',
}: {
  children?: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'danger'
  size?: 'sm' | 'md'
  disabled?: boolean
  icon?: LucideIcon
  title?: string
  type?: 'button' | 'submit'
  full?: boolean
  className?: string
}) {
  const variantClass =
    variant === 'primary' ? 'ut-btn-primary' : variant === 'danger' ? 'ut-btn-danger' : ''
  return (
    <button
      type={type}
      className={`ut-btn ${variantClass} ${size === 'sm' ? 'ut-btn-sm' : ''} ${full ? 'w-full' : ''} ${className}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {Icon && <Icon size={size === 'sm' ? 11 : 13} strokeWidth={2} aria-hidden />}
      {children}
    </button>
  )
}

/** Группа вкладок */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string; icon?: LucideIcon; badge?: number }>
  active: T
  onChange: (id: T) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1" role="tablist">
      {tabs.map((t) => {
        const Icon = t.icon
        const isActive = t.id === active
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={isActive}
            className={`ut-tab ${isActive ? 'ut-tab-active' : ''}`}
            onClick={() => onChange(t.id)}
            data-state={isActive ? 'active' : undefined}
          >
            <span className="flex items-center gap-1">
              {Icon && <Icon size={12} strokeWidth={2} aria-hidden />}
              {t.label}
              {t.badge !== undefined && t.badge > 0 && (
                <span className="ut-metric rounded-full bg-surface-alt px-1.5 text-[10px] leading-4 text-ink-muted">
                  {t.badge}
                </span>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** Разделитель */
export function Divider({ label }: { label?: string }) {
  if (!label) return <hr className="my-2 border-line" />
  return (
    <div className="my-2 flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
        {label}
      </span>
      <hr className="flex-1 border-line" />
    </div>
  )
}

/** Пустое состояние списка */
export function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center px-3 py-6 text-center text-[11px] text-ink-muted">
      {children}
    </div>
  )
}
